import { Entity, IEntity } from '@lycoris-nubila/ngx-lycoris';
import { first } from 'lodash-es';
import { Observable, of } from 'rxjs';

import { PhaseType } from 'phase/model/phase.type';

import { IPhaseI18n, PhaseI18n } from './phase-i18n';
import { ISection, Section } from './section/section';

export interface IPhase extends IEntity {
	readonly type: PhaseType;
	readonly position: number;
	readonly organizationId: string;
	readonly sectionsId: ReadonlyArray<string>;
	readonly localizations: ReadonlyArray<IPhaseI18n>;
	readonly sections$: Observable<ISection[]>;
	readonly localization$: Observable<IPhaseI18n>;
	readonly cssColorClass: string;
	readonly cssBgColorClass: string;
	readonly cssBorderColorClass: string;
	localization(language: string): IPhaseI18n;
}

export class Phase extends Entity implements IPhase {
	private _type: PhaseType;
	private _position: number;
	private _sectionsId: string[];
	private _organizationId: string;
	private _localizations: PhaseI18n[];
	private _sections$: Observable<Section[]>;
	private _localization$: Observable<PhaseI18n>;
	private _cssColorClass: string;
	private _cssBgColorClass: string;
	private _cssBorderColorClass: string;

	constructor(
		id: string,
		type: PhaseType,
		position: number,
		sectionsId: string[],
		organizationId: string,
		updateTimestamp: number,
		localizations: PhaseI18n[],
		sections$: Observable<Section[]> = of([]),
		localization$: Observable<PhaseI18n> = of(null)
	) {
		super(id, null, updateTimestamp);
		this._type = type;
		this._position = position;
		this._sectionsId = sectionsId;
		this._organizationId = organizationId;
		this._localizations = localizations;
		this._sections$ = sections$;
		this._localization$ = localization$;

		switch (type) {
			case PhaseType.REFLECT:
				this._cssColorClass = 'p-color-primary-pastel';
				this._cssBgColorClass = 'p-background-color-primary';
				this._cssBorderColorClass = 'p-border-color-primary';
				break;
			case PhaseType.PREPARE:
				this._cssColorClass = 'p-color-primary';
				this._cssBgColorClass = 'p-background-color-primary-pastel';
				this._cssBorderColorClass = 'p-border-color-primary-pastel';
				break;
			case PhaseType.CARRY_OUT:
				this._cssColorClass = 'p-color-primary-variant';
				this._cssBgColorClass = 'p-background-color-primary-variant-pastel';
				this._cssBorderColorClass = 'p-border-color-primary-variant-pastel';
				break;
		}
	}

	public get type(): PhaseType {
		return this._type;
	}

	public get position(): number {
		return this._position;
	}

	public get sectionsId(): string[] {
		return this._sectionsId;
	}

	public get organizationId(): string {
		return this._organizationId;
	}

	public get localizations(): PhaseI18n[] {
		return this._localizations;
	}

	public get sections$(): Observable<ISection[]> {
		return this._sections$;
	}

	public get localization$(): Observable<PhaseI18n> {
		return this._localization$;
	}

	public get cssColorClass(): string {
		return this._cssColorClass;
	}

	public get cssBgColorClass(): string {
		return this._cssBgColorClass;
	}

	public get cssBorderColorClass(): string {
		return this._cssBorderColorClass;
	}

	public localization(language: string): IPhaseI18n {
		return (
			this._localizations.find((l) => l.locale === language) ??
			first(this._localizations)
		);
	}
}
